/**
 * Coming back to Ask after one of its actions (user, 2026-10-15: "the AI
 * suggests two actions, I click one … when I close it, it also closes the AI
 * drawer"). Clicking an action shrinks Ask to a pill instead of closing it;
 * whatever the action opened (a Fill with AI drawer, a form, a page) reports
 * back here what happened, and Ask returns to the same answer with that
 * action marked — when there's something left to do.
 *
 *   opened → the form or drawer is open
 *   filled → its values went into the form (the form still waits for Save)
 *   saved  → the record was saved
 *   closed → closed with nothing filled
 *   skipped → the person said they won't do it (Skip in Ask); counts as done
 */

export type AskActionStatus = "opened" | "filled" | "saved" | "closed" | "skipped";

export const ASK_ACTION_EVENT = "crx:ask-action";

export interface AskActionEvent {
    key: string;
    status: AskActionStatus;
}

interface Active {
    key: string;
    /** The save request that means "saved" for this action ("contacts", "providers/7"), when known. */
    saves: RegExp | null;
    filled: boolean;
}

let active: Active | null = null;

const emit = (key: string, status: AskActionStatus) => {
    window.dispatchEvent(new CustomEvent<AskActionEvent>(ASK_ACTION_EVENT, { detail: { key, status } }));
};

/**
 * An action from Ask is starting (one at a time; a new one replaces the last).
 * @param key - The action's key in the conversation.
 * @param saves - The request that saves what it fills, when known.
 */
export const startAskAction = (key: string, saves: RegExp | null = null): void => {
    active = { key, saves, filled: false };
    emit(key, "opened");
};

/**
 * What happened to the action Ask started — called by the drawers and forms it
 * opens. Nothing happens when no Ask action is going on (the same drawers open
 * from their own pages too). Closing a drawer after its values went in keeps
 * the action "filled": the form still waits for Save.
 * @param status - filled, saved or closed.
 */
export const reportAskAction = (status: Exclude<AskActionStatus, "opened">): void => {
    if (!active) return;
    if (status === "closed" && active.filled) return;
    if (status === "filled") active.filled = true;
    const { key } = active;
    if (status === "saved" || status === "closed") active = null;
    emit(key, status);
};

/**
 * A save went through (called by the request helper for every successful
 * write): when it's the save the running action waits for, it's "saved".
 * @param method - POST, PUT, PATCH …
 * @param url - The API path as called ("contacts", "providers/7?x=1").
 */
export const noteSaveRequest = (method: string, url: string): void => {
    if (!active?.saves || method === "GET") return;
    const path = String(url ?? "").replace(/^\/+/, "").split("?")[0];
    if (active.saves.test(path)) reportAskAction("saved");
};

/** Whether an action from Ask is going on (for the pill's wording). */
export const askActionKey = (): string | null => active?.key ?? null;

/** Stop following the action under way (Hide, a new question): its save no longer brings Ask back. */
export const cancelAskAction = (): void => {
    active = null;
};

/**
 * Ask is minimised with something left to do (user, 2026-10-15: "you will have a hard time finding it … user
 * shouldn't open the chat again from somewhere else"). The page shows it where people look: inside the Save bar
 * when there is one, else a gold pill; the header and search offer "Back to Ask" — the same answer, not a new chat.
 */
export interface AskWaiting {
    /** The question the waiting answer was for, shortened. */
    title: string | null;
    /** Its actions still to do, besides the one under way. */
    left: number;
    /** The action under way put its values in a form that waits for Save. */
    filled: boolean;
    /** Ask was closed on purpose: only the header and search remind (no Save bar chip, no pill). */
    quiet: boolean;
}

export const ASK_WAITING_EVENT = "crx:ask-waiting";
export const RESUME_ASK_EVENT = "crx:ask-resume";
export const DISMISS_ASK_EVENT = "crx:ask-dismiss";

let waiting: AskWaiting | null = null;
let hosts = 0;

const announce = () => {
    window.dispatchEvent(new CustomEvent(ASK_WAITING_EVENT));
};

/**
 * What Ask is waiting on (set by the Ask drawer; null when nothing).
 * @param next - The waiting answer's summary, or null.
 */
export const setAskWaiting = (next: AskWaiting | null): void => {
    const same = next === waiting
        || (!!next && !!waiting && next.title === waiting.title && next.left === waiting.left && next.filled === waiting.filled && next.quiet === waiting.quiet);
    if (same) return;
    waiting = next;
    announce();
};

/** What Ask is waiting on, or null. */
export const askWaiting = (): AskWaiting | null => waiting;

/**
 * A Save bar on the page shows the Ask reminder, so the floating pill stays away (it covered Save).
 * @returns The release, for when the bar goes.
 */
export const hostAskReminder = (): (() => void) => {
    hosts += 1;
    announce();
    let isReleased = false;
    return () => {
        if (isReleased) return;
        isReleased = true;
        hosts -= 1;
        announce();
    };
};

/** Whether a Save bar carries the Ask reminder right now. */
export const isAskReminderHosted = (): boolean => hosts > 0;

/** "Back to Ask" from anywhere: the same chat at the waiting answer, never a new one. */
export const resumeAsk = (): void => {
    window.dispatchEvent(new CustomEvent(RESUME_ASK_EVENT));
};

/** "Not now" (the Save bar chip, the pill): stop reminding, and a later save doesn't bring Ask back. */
export const dismissAsk = (): void => {
    window.dispatchEvent(new CustomEvent(DISMISS_ASK_EVENT));
};

/**
 * The reminder's second line: "1 more step after you save", "save it to finish", "2 more steps".
 * @param w - What Ask is waiting on.
 */
export const waitingNote = (w: AskWaiting): string => {
    const steps = `${w.left} more step${w.left === 1 ? "" : "s"}`;
    if (w.filled) return w.left ? `${steps} after you save` : "save it to finish";
    return w.left ? steps : "back to the answer";
};

const PLURAL: Record<string, string> = { provider: "providers", client: "clients", company: "companies", lead: "leads", contact: "contacts" };

/**
 * The save request that completes an action's form — a new contact is
 * "POST contacts", an updated provider "PUT providers/7" — or null when the
 * form reports it itself (or there's no single save).
 * @param go - The action: a button's target, or a draft card's kind and values.
 */
export const savePatternFor = (go: { type?: string; kind?: string; id?: number; values?: Record<string, any> }): RegExp | null => {
    const values = go.values ?? {};
    const exact = (path: string) => new RegExp(`^${path}/?$`);
    switch (go.type ?? go.kind) {
        case "contact": return exact("contacts");
        case "lead": return exact("leads");
        case "record_new": return go.kind && PLURAL[go.kind] ? exact(PLURAL[go.kind]) : null;
        case "record_update": return go.kind && PLURAL[go.kind] && go.id ? exact(`${PLURAL[go.kind]}/${go.id}`) : null;
        // draft cards
        case "request": return exact("mission-requests");
        case "request_edit": return values.missionRequestId ? exact(`mission-requests/${values.missionRequestId}`) : null;
        case "mission": return /^missions(?:\/full)?\/?$/;
        case "lead_edit": return values.leadId ? exact(`leads/${values.leadId}`) : null;
        case "org_edit": return values.organizationId ? new RegExp(`^(?:clients|providers)/${values.organizationId}/?$`) : null;
        case "contact_edit": return values.userDetailId ? exact(`contacts/${values.userDetailId}`) : null;
        case "mission_dates": case "movements": case "driver": case "vehicle": case "movement_edit": case "incident":
            return values.missionId ? new RegExp(`^missions/${values.missionId}(?:/(?!.*search)[\\w/-]*)?/?$`) : null;
        default: return null;
    }
};

/** Drafts a page applies the moment it takes them: taking one means its values are in the form. */
export const APPLIED_ON_TAKE = new Set(["request", "mission", "mission_dates", "movements", "driver", "incident", "lead_edit", "request_edit", "movement_edit", "vehicle"]);
